/**
 * Drill-down modal listing the dataset records that match one of three
 * stat-tile filters on `ChannelDatasetCard` (B16-FE). The canonical
 * status list per filter lives in `STAT_FILTERS` in `ChannelDatasetCard`
 * — keep them in sync via that constant rather than duplicating the
 * list here.
 *
 * Owns its own `offset` state; parent's conditional render unmounts on
 * close so state resets cleanly. Width is `sm:max-w-[1280px]` to fit the
 * 5-column record table comfortably without horizontal scroll on
 * typical desktop viewports.
 *
 * Annotator name resolution is the soft dep on B15-FE: calls
 * `useUsersById` against the page's union of annotator ids; falls back
 * to truncated uuids when names are missing (no crash, no UX cliff).
 */
import React from 'react';
import { ChevronDown } from 'lucide-react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../ui/table';
import { formatTimeAgo } from '../../../../../../utils/dateUtils';
import { statusBadge } from '../../../annotate/components/statusBadge';
import { useUsersById } from '../../../annotate/hooks/useUsers';
import type { UserSummary } from '../../../annotate/types';
import { useVersionDetail } from '../../../hooks/useTaxonomy';
import { useLabelNames } from '../../../shared/useLabelNames';
import { useChannelRecords } from '../../hooks/useDatasets';
import type {
  DatasetRecord,
  GoLabDatasetRecordStatus,
} from '../../types';
import type { ChannelStatFilterKey } from './ChannelDatasetCard';
import RecordDetailPanel from './RecordDetailPanel';


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelDatasetId: string;
  channelName: string;
  /**
   * Collection's pinned taxonomy, used to resolve a record's label ids to
   * human-readable names in the inline detail panel (C7-FE). One lookup
   * shared by every row; an id missing from the resolved map degrades to
   * the raw id.
   */
  taxonomyKey: string;
  taxonomyVersionId: string;
  filterKey: ChannelStatFilterKey;
  statuses: GoLabDatasetRecordStatus[];
  title: string;
}


// Tighter page size than the queue page's 50 — sized so the table
// fits the modal's `max-h-[480px]` body without internal scrolling.
export const PAGE_SIZE = 10;


/** "abcd1234…" — used when annotator name lookup is missing. */
const uuidFallback = (id: string): string => `${id.slice(0, 8)}…`;

const annotatorLabel = (
  id: string | null,
  byId: Record<string, UserSummary> | undefined,
): string => {
  if (!id) return '—';
  const name = byId?.[id]?.name;
  if (name && name.trim().length > 0) return name;
  return uuidFallback(id);
};


const ChannelRecordsModal: React.FC<Props> = ({
  open,
  onOpenChange,
  channelDatasetId,
  channelName,
  taxonomyKey,
  taxonomyVersionId,
  filterKey,
  statuses,
  title,
}) => {
  // Offset state. The parent currently unmounts the modal on close, so
  // useState resets naturally; the useEffect below is belt-and-braces
  // for any future change that keeps the modal mounted across filters.
  const [offset, setOffset] = React.useState(0);
  React.useEffect(() => {
    setOffset(0);
  }, [filterKey]);

  // C7-FE: which record row (if any) is expanded into its inline detail
  // panel. Single-row-expand; paging or a filter change collapses it.
  const [expandedRecordId, setExpandedRecordId] = React.useState<string | null>(
    null,
  );
  React.useEffect(() => {
    setExpandedRecordId(null);
  }, [offset, filterKey]);

  const params = React.useMemo(
    () => ({ statuses, limit: PAGE_SIZE, offset }),
    [statuses, offset],
  );

  const { data, isLoading, isError, refetch } = useChannelRecords(
    channelDatasetId,
    params,
  );

  // Resolve the collection's pinned taxonomy once for the whole modal so
  // an expanded record's label ids render as human-readable names. Shared
  // by every row; the panel falls back to raw ids for anything missing.
  const versionDetail = useVersionDetail(taxonomyKey, taxonomyVersionId);
  const dimensions = React.useMemo(
    () => versionDetail.data?.taxonomyData?.dimensions ?? [],
    [versionDetail.data],
  );
  const labelNamesByDim = useLabelNames(dimensions);

  // Page's unique annotator ids → one batched name-lookup request via
  // B15-FE's hook. Empty array short-circuits via the hook's `enabled`
  // guard, so missing annotators don't cost a round-trip.
  const annotatorIds = React.useMemo(() => {
    const ids = new Set<string>();
    (data?.items ?? []).forEach((r) => {
      if (r.firstAnnotatorId) ids.add(r.firstAnnotatorId);
      if (r.secondAnnotatorId) ids.add(r.secondAnnotatorId);
    });
    return Array.from(ids);
  }, [data?.items]);

  const { data: namesByAnnotatorId } = useUsersById(annotatorIds);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[1280px]"
        data-testid="channel-records-modal"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {channelName} · {total.toLocaleString()}{' '}
            {total === 1 ? 'matching record' : 'matching records'}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[480px] overflow-auto">
          {isLoading && (
            <div
              className="py-12 text-center text-sm text-muted-foreground"
              data-testid="channel-records-loading"
            >
              Loading records…
            </div>
          )}

          {isError && (
            <div
              className="py-12 text-center text-sm text-rose-600"
              data-testid="channel-records-error"
            >
              <p>Failed to load records.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetch()}
                data-testid="channel-records-retry"
              >
                Try again
              </Button>
            </div>
          )}

          {!isLoading && !isError && data && data.items.length === 0 && (
            <div
              className="py-12 text-center text-sm text-muted-foreground"
              data-testid="channel-records-empty"
            >
              No records in this filter.
            </div>
          )}

          {!isLoading && !isError && data && data.items.length > 0 && (
            <Table className="table-fixed" data-testid="channel-records-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Verbatim
                  </TableHead>
                  <TableHead className="w-[150px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="w-[130px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    1st annotator
                  </TableHead>
                  <TableHead className="w-[130px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    2nd annotator
                  </TableHead>
                  <TableHead className="w-[120px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Updated
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((record: DatasetRecord) => {
                  const badge = statusBadge(record.status);
                  const isRowExpanded = expandedRecordId === record.id;
                  const toggleRow = () =>
                    setExpandedRecordId((cur) =>
                      cur === record.id ? null : record.id,
                    );
                  return (
                    <React.Fragment key={record.id}>
                      {/* The row stays a real table row (keeps its `row`
                          semantics + header association); whole-row click is a
                          convenience. The accessible, keyboard-native expander
                          is the chevron <button> in the first cell — it owns
                          `aria-expanded`/`aria-controls` and stops propagation
                          so it doesn't double-toggle via the row. */}
                      <TableRow
                        data-testid={`channel-records-row-${record.id}`}
                        title={record.id}
                        onClick={toggleRow}
                        className={`cursor-pointer hover:bg-muted/30 ${
                          isRowExpanded ? 'bg-muted/30' : ''
                        }`}
                      >
                        <TableCell
                          className="max-w-[420px] text-[13px] text-foreground"
                          title={record.verbatim}
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRow();
                              }}
                              aria-expanded={isRowExpanded}
                              aria-controls={`channel-records-detail-row-${record.id}`}
                              aria-label={
                                isRowExpanded
                                  ? 'Hide record details'
                                  : 'Show record details'
                              }
                              data-testid={`channel-records-expander-${record.id}`}
                              className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                            >
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${
                                  isRowExpanded ? 'rotate-180' : ''
                                }`}
                                aria-hidden="true"
                              />
                            </button>
                            <span className="truncate">{record.verbatim}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </TableCell>
                        <TableCell
                          className="text-[12px] text-muted-foreground"
                          data-testid={`channel-records-first-annotator-${record.id}`}
                        >
                          {annotatorLabel(
                            record.firstAnnotatorId,
                            namesByAnnotatorId,
                          )}
                        </TableCell>
                        <TableCell
                          className="text-[12px] text-muted-foreground"
                          data-testid={`channel-records-second-annotator-${record.id}`}
                        >
                          {annotatorLabel(
                            record.secondAnnotatorId,
                            namesByAnnotatorId,
                          )}
                        </TableCell>
                        <TableCell className="text-[12px] text-muted-foreground">
                          {formatTimeAgo(
                            record.finalisedAt ?? record.createdAt,
                          )}
                        </TableCell>
                      </TableRow>
                      {isRowExpanded && (
                        <TableRow
                          id={`channel-records-detail-row-${record.id}`}
                          data-testid={`channel-records-detail-row-${record.id}`}
                          className="hover:bg-transparent"
                        >
                          <TableCell colSpan={5} className="bg-muted/20 p-0">
                            <RecordDetailPanel
                              record={record}
                              dimensions={dimensions}
                              labelNamesByDim={labelNamesByDim}
                              namesByAnnotatorId={namesByAnnotatorId}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={!canPrev}
            data-testid="channel-records-prev"
          >
            Previous
          </Button>
          <span
            className="text-xs text-muted-foreground"
            data-testid="channel-records-page-label"
          >
            Page {currentPage} of {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            disabled={!canNext}
            data-testid="channel-records-next"
          >
            Next
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChannelRecordsModal;
